import './CurationCarousel.scss'

import React from 'react'
import classNames from 'classnames'
import { isNil } from 'lodash'

import Carousel from './Carousel'
import Translate from './Translate'
import LinkWithImage from './LinkWithImage'
import EpisodeListInfo from './EpisodeListInfo'
import CCLanguages from './CCLanguages'
import LinkWrapper from './LinkWrapper'

import { getTranslateValue } from '../utils/locale'
import { buildEpisodePageUrl } from '../utils/episode'
import { getSizedImageUrl } from '../utils/misc'
import { isCarouselProgram, isCarouselCWEpisode } from '../utils/typeGuards'
import { PosterType, ReleaseStatus } from '../constants/enums'
import { DEFAULT_IMAGE_SIZE } from '../constants/images'
import { CurationItemFragment, CurationCarouselContentsFragment, Maybe } from '../types/generated'

const createImageUrl = (imageUrl?: Maybe<string>) =>
  getSizedImageUrl(imageUrl, DEFAULT_IMAGE_SIZE.WIDTH, DEFAULT_IMAGE_SIZE.HEIGHT)

const getProgressRatio = (content: CurationCarouselContentsFragment) => {
  if (isCarouselCWEpisode(content) && !isNil(content.progressRatio)) {
    return content.progressRatio
  }
}

interface Props {
  isLoading: boolean
  curation: CurationItemFragment
  slideSpeed?: number
  linkQuerystring?: string
}

export default function CurationCarousel({ isLoading, slideSpeed = 500, curation, linkQuerystring }: Props) {
  const connectQuerystring = () => (linkQuerystring ? `?${linkQuerystring}` : '')
  const contents = curation.carousels

  return (
    <div className="CurationCarousel">
      {curation.titleUrl ? (
        <LinkWrapper url={curation.titleUrl}>
          <h3 className="CurationCarousel__title">
            <Translate target={curation} fieldKey="title" />
          </h3>
        </LinkWrapper>
      ) : (
        <h3 className="CurationCarousel__title">
          <Translate target={curation} fieldKey="title" />
        </h3>
      )}
      {!isLoading && contents ? (
        <Carousel slideSpeed={slideSpeed}>
          {contents.map((content, index) => (
            <div key={`${getTranslateValue(content, 'title')}-${index}`} className="CurationCarousel__itemWrapper">
              {isCarouselProgram(content) && (
                <LinkWithImage
                  link={`/program/${content.slug}${
                    curation.programDirectPlay ? '/direct-play' : ''
                  }${connectQuerystring()}`}
                  imageUrl={createImageUrl(getTranslateValue(content.images, 'poster'))}
                  alt={getTranslateValue(content, 'title')}
                  posterType={curation.ratio === 'portrait' ? PosterType.PORTRAIT : PosterType.LANDSCAPE}
                  isShowLetterbox
                >
                  <h4 className="CurationCarousel__itemTitle">{getTranslateValue(content, 'title')}</h4>
                  <EpisodeListInfo
                    isSeries={!!content.meta?.isSeries}
                    releaseStatus={content.releaseStatus as ReleaseStatus}
                    latestEpisodeTitle={getTranslateValue(content.latestEpisode, 'title') ?? ''}
                    episodeTotal={content.meta?.episodeTotal ?? 0}
                    produceYear={content.produceYear}
                    produceCountries={content.meta?.produceCountries ?? []}
                  />
                  <CCLanguages ccLanguages={content.meta?.ccLanguages ?? []} />
                </LinkWithImage>
              )}
              {!isCarouselProgram(content) && (
                <LinkWithImage
                  link={`${buildEpisodePageUrl(
                    content.program.slug,
                    content.kind,
                    content.number,
                    content.part
                  )}${connectQuerystring()}`}
                  imageUrl={createImageUrl(content.images?.thumbnail)}
                  alt={getTranslateValue(curation, 'title')}
                  videoDuration={content.videoDuration}
                  progressRatio={getProgressRatio(content)}
                  posterType={curation.ratio === 'portrait' ? PosterType.PORTRAIT : PosterType.LANDSCAPE}
                  isShowLetterbox
                >
                  <h4 className="CurationCarousel__itemTitle">{getTranslateValue(content.program, 'title')}</h4>
                  {getTranslateValue(content, 'title') && (
                    <h5 className="CurationCarousel__itemSubTitle">{getTranslateValue(content, 'title')}</h5>
                  )}
                  <CCLanguages ccLanguages={content.videoCcLanguages} />
                </LinkWithImage>
              )}
            </div>
          ))}
        </Carousel>
      ) : (
        <div
          className={classNames('CurationCarousel__holder', {
            'CurationCarousel__holder--portrait': curation.ratio === 'portrait',
          })}
        />
      )}
    </div>
  )
}
